import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createRecordedVoiceoverVideoSkill: Skill = {
    name: SKILL_NAMES.createRecordedVoiceoverVideo,
    description:
        "Video built over the user's own recorded voiceover audio: the narration is fixed, and every second of it gets visuals. Load whenever the user attaches or points to a voice recording to build a video on — not a video of someone speaking (talking head), and not an audiogram/waveform treatment.",
    kind: 'video',
    surfaces: ['sketch'],
    instructions: `# Create Recorded Voiceover Video (Lumen5 Video Recipe)
The user recorded the narration; your job is the picture. The recording is fixed —
never reworded, re-voiced, trimmed, or replaced — so there is no script to write and no
duration to pick. What remains is covering **every second** of it with scenes that
illustrate what is being said at that moment. A recording can run many minutes; the
video is only finished when all of it is covered.

Load \`${SKILL_NAMES.recordedVoiceoverVideoBasics}\` and \`${SKILL_NAMES.voiceoverVideoBasics}\`
for the scene-group mechanics this recipe builds on.

## Setup
The recording already settles narration, voice, tone, length and the closing line, so
don't ask about any of them. Gather only what is still open, in one
\`${TOOL_NAMES.askUserQuestion}\` call (the \`${SKILL_NAMES.gatherRequirements}\` rule) — and skip the
call entirely when nothing is:
- **Aspect ratio** — type \`aspect_ratio\`, unless the user already gave one.
- **Template** — only if the user named none; pick one that suits the subject.
Then \`${TOOL_NAMES.createBlankVideo}\` and \`${TOOL_NAMES.addRecordedVoiceoverSceneGroup}\` with the
recording's \`audio_id\` — one scene group for the whole recording. When the user attached the
recording, that attachment's \`ID\` is the \`audio_id\`: never call
\`${TOOL_NAMES.searchUploadedVoiceovers}\` or build on some other upload instead.

## Plan the scene list from the whole transcript
The new group's base scene \`covers\` the full transcript. Before searching for any media,
split that transcript into **consecutive spans, back to back, that together contain every
word and every \`<pause seconds="N"/>\`**. A span starts on the word right after the previous
span's last word; a pause between two sentences belongs to the span it precedes.

**Each span is 8-12 words** — one clause or short sentence, about three to five seconds of
speech. Never more than 15 words: a longer sentence splits at its next comma or
conjunction. A span of 20+ words is a scene the viewer stares at for eight seconds or more,
which is the defect this recipe exists to prevent.

That makes the scene count arithmetic, not a style choice: **the transcript's word count
divided by 10**. A 60-second recording (~150 words) is ~15 scenes; a 7-minute lesson
(~1,200 words) is ~100 scenes, averaging under five seconds each. Count before you start and
hold yourself to it. There is no time limit and no tool-call limit on this build — you have
as many turns as the recording needs, so never trade scene count for speed.

## Build section by section
The whole-video job sounds large, but it breaks into small, identical pieces: a minute of
narration is ~150 words, so each section is one \`${TOOL_NAMES.addVoiceoverScene}\` call of
12-15 scenes in which you write out ~150 words. A 7-minute recording is about eight of these
calls — no single call is big, so never widen spans to save typing.

Work through the recording in those sections, in order. For each section:
1. Add all of its scenes in **one** \`${TOOL_NAMES.addVoiceoverScene}\` call, every span 8-12 words
   (15 at most — a call with a longer span is wrong; split it before sending). Rotate designs
   across them (see Scene design).
2. Run a few stock searches for the section's ideas (\`${SKILL_NAMES.findStockMedia}\`). Short
   scenes don't each need a new clip: two or three consecutive scenes on the same thought
   can keep the same clip (a continuing shot), and text-led designs need none. Don't bring a
   clip back later in the video once it has moved on.
3. Set each scene's media and text by the \`scene_id\` the add returned.
If an add reports scenes it couldn't place, fix those spans (usually a missing or
misspelled pause tag) and retry just them before moving on — never skip them.

Only move to Finishing after the last section's scenes are in. Reviewing or linting a
half-built video tells you nothing; it doesn't count toward finishing.

## Scene design
- Rotate across design categories — title, media, collage, text, quote, list,
  data/chart, animated captions, icon — rather than leaning on two or three. Never the
  same design on two consecutive scenes.
- **Open on a title scene** over the recording's first sentence, whose text names what
  the video is about. Close on the recording's own closing lines — its sign-off is the
  outro; don't add a CTA it doesn't say.
- A design's text assets must fit the words going in them; a design missing a needed
  asset is the wrong design.
- A Quote design only for an actual attributed quotation. List designs only when every
  item is about six words or fewer; otherwise one scene per item.

## Text
- Text goes on the moments worth reading — section headings ("Skill two: observation"), key
  terms, numbers, takeaways — and **about one scene in four carries it**: 3-4 text scenes in
  each minute's section. Put those on text-led designs (title, text, list, quote, icon) so the
  words are the point of the scene; don't make every scene a full-bleed clip.
- The rest can be media-led, but a media-led scene uses a design built for media alone, or
  shows the spoken line's key phrase in its text slot. Blanking a design's text to dodge a placeholder
  warning is not a fix — a video that is nothing but clips under a voice reads as unfinished.
- When a scene does show text, it is display copy drawn from what is spoken during that
  scene — its key phrase, number, or payoff, not a transcript of the line, and never a
  claim or figure the speaker didn't make. Keep it to one idea at a
  readable size (around 60 or above).
- **Never use em dashes (—)** in on-screen text.

## Media
- Each clip should obviously fit what is being said over it. Vaguely-on-topic is not
  enough; search again rather than settle.
- Load \`${SKILL_NAMES.findUserMedia}\` first when the user has their own media,
  \`${SKILL_NAMES.findStockMedia}\` before the first stock search, and \`${SKILL_NAMES.findDamMedia}\` when a
  DAM is connected.
- Reframe so nothing important sits under text (\`${SKILL_NAMES.cropMedia}\`).

## Captions and music
- **Captions** off by default (\`${TOOL_NAMES.setCaptions}\` with \`enabled: false\`) unless the
  user asks for them.
- **Music**: a quiet bed that suits the subject, kept low under the speech (around 10-15%
  volume), unless the user says otherwise. Leave the voice itself at full volume; if the user
  wants the narration quieter, or the music ducked under just this group, use
  \`${TOOL_NAMES.setSceneGroupVolume}\`.
- Colors and styling come from the template or the user's brand kit
  (\`${SKILL_NAMES.applyBrandKit}\`).

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` and review the scenes you built, fixing what it returns. Then run
\`${TOOL_NAMES.lintVideo}\` as the **last** step and fix every error. \`overlay_coverage\` in particular
must come back clean: any range it lists is narration playing over the bare waveform
placeholder. Cover each one — extend a neighbouring scene with \`${TOOL_NAMES.setSceneRange}\` or add
scenes for it — then lint again. The video is done only when a lint run after your last
edit reports no errors.`,
};
